import React, { ChangeEvent, useEffect } from "react";
import { TextInput } from "@src/components/elements";
import { TableProps } from "@src/components/modules/Table/Table";
import {
  HeaderProps,
  TableOptions,
  useBlockLayout,
  useExpanded,
  useFilters,
  useFlexLayout,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { useSticky } from "@src/components/modules/Table/useSticky";

type Args<T extends Record<string, unknown>> = Pick<
  TableProps<T>,
  | "setFilters"
  | "filters"
  | "canFilter"
  | "fixedWidth"
  | "columns"
  | "data"
  | "getInstanceCallback"
  | "filterTypes"
  | "autoResetSortBy"
  | "initialPage"
  | "pageSize"
  | "isExternallyControlled"
  | "title"
  | "canExport"
  | "headerSlot"
  | "onSortByChange"
> & {
  filtersFromOutside?: { id: string; value?: string }[];
  setFilterOutside?: (f: { id: string; value?: string }) => void;
};

export const useTableConfiguration = <T extends Record<string, unknown>>({
  setFilters,
  filters,
  setFilterOutside,
  filtersFromOutside,
  canFilter,
  fixedWidth,
  columns,
  data,
  initialPage,
  autoResetSortBy,
  filterTypes,
  getInstanceCallback,
  pageSize,
  canExport,
  headerSlot,
  isExternallyControlled,
  title,
  onSortByChange,
}: Args<T>) => {
  const defaultColumn = {
    Filter: function defaultFilterColumn(
      props: HeaderProps<Record<string, unknown>>,
    ) {
      const { filterValue, preFilteredRows, setFilter, id } = props.column;
      const count = preFilteredRows.length;

      const inputVal = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        return value.trim() === "" ? undefined : value;
      };

      return (
        <TextInput
          name=""
          hideError={true}
          initialValue={
            filtersFromOutside
              ? filtersFromOutside.find((f) => f.id === id)?.value || ""
              : filterValue || ""
          }
          onChange={(e) => {
            const val = inputVal(e);
            setFilter(val);
            setFilterOutside?.({ id, value: val });

            if (setFilters) {
              setFilters((filters) => {
                if (val === undefined) {
                  return filters?.filter((f) => f.id !== id);
                } else {
                  if (filters.find((f) => f.id === id)) {
                    return filters?.map((f) => {
                      if (f.id === id) {
                        return { id: id, value: val };
                      }
                      return f;
                    });
                  } else {
                    return [...(filters ?? []), { id: id, value: val }];
                  }
                }
              });
            }
          }}
          placeholder={`Search ${count} records...`}
        />
      );
    },
  };

  const plugins = [
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    useSticky,
  ];

  if (canFilter) {
    plugins.splice(0, 0, useFilters);
  }

  if (fixedWidth) {
    plugins.push(useBlockLayout);
  } else {
    plugins.push(useFlexLayout);
  }

  const tableInstance = useTable<T>(
    {
      columns,
      getInstanceCallback,
      data,
      defaultColumn,
      filterTypes,
      autoResetSortBy,
      initialState: {
        hiddenColumns: ["id"],
        pageIndex: initialPage - 1,
        pageSize,
        filters: filters ? filters ?? [] : filtersFromOutside ?? [],
        sortBy: [],
      },
      autoResetPage: true,
      autoResetFilters: false,
      manualSortBy: !!onSortByChange,
    } as TableOptions<T>,
    ...plugins,
  );

  const showTableHeader = Boolean(
    !isExternallyControlled && (title || canFilter || canExport || headerSlot),
  );

  useEffect(() => {
    if (onSortByChange) {
      onSortByChange(tableInstance.state.sortBy);
    }
  }, [onSortByChange, tableInstance.state.sortBy]);

  return {
    ...tableInstance,
    defaultColumn,
    showTableHeader,
  };
};
